// client/src/coms/views/watch_together_parts/watchRelayRoom.js
// The transport for a Watch Together session that runs through the server (server/routes/activities.js)
// instead of a voice room's data channel. It offers the small part of a LiveKit Room that the existing
// Watch Together code uses - localParticipant.publishData, remoteParticipants with their 'vox.perms'
// attributes, on/off for participant events - so the player, queue, providers and drift correction are
// unchanged. The server checks and relays every packet and sends the authoritative state on (re)connect,
// so it works the same for people in a voice channel and people who joined with a code.
import {
  WATCH_QUEUE_PACKET_TOPIC,
  applyAuthoritativeWatchSnapshot,
  applyIncomingWatchQueuePacket,
  applyWatchControl,
  bindWatchTransport,
  resetWatchQueueState,
  unbindWatchTransport,
} from './sessionQueue';
import { applyIncomingRemoteSpotifyPlaybackPacket } from './remoteSpotifyPlaybackRouter';

const SPOTIFY_TOPIC = 'pa-watch-spotify-remote';

function participantFrom(p) {
  return {
    identity: String(p.identity),
    name: String(p.name || ''),
    userId: Number(p.userId) || 0,
    attributes: { 'vox.perms': (p.perms || []).join(',') },
    metadata: '',
  };
}

const sameParticipant = (a, b) => a && b && a.name === b.name && a.attributes['vox.perms'] === b.attributes['vox.perms'];

// callbacks: { onActivity(activity), onEnded(reason), onKicked(), onConnection('connecting'|'connected'|'reconnecting'|'closed') }
export function createWatchRelayRoom(sessionId, callbacks = {}) {
  const base = `/api/activities/watch/${encodeURIComponent(sessionId)}`;
  const handlers = new Map();
  let source = null;
  let closed = false;
  const encoder = new TextEncoder();
  const decoder = new TextDecoder();

  const room = {
    name: `watch-${sessionId}`,
    sessionId,
    isWatchRelay: true,
    state: 'connecting',
    localParticipant: null,
    remoteParticipants: new Map(),
    on(event, fn) {
      if (!handlers.has(event)) handlers.set(event, new Set());
      handlers.get(event).add(fn);
      return room;
    },
    off(event, fn) {
      const set = handlers.get(event);
      if (set) set.delete(fn);
      return room;
    },
    connect,
    disconnect,
  };

  function emit(event, ...args) {
    const set = handlers.get(event);
    if (!set) return;
    Array.from(set).forEach((fn) => {
      try {
        fn(...args);
      } catch {}
    });
  }

  function setConnection(state) {
    room.state = state;
    if (typeof callbacks.onConnection === 'function') callbacks.onConnection(state);
  }

  async function publish(bytes, options = {}) {
    let packet;
    try {
      packet = JSON.parse(decoder.decode(bytes));
    } catch {
      return;
    }
    const body = { topic: options.topic || WATCH_QUEUE_PACKET_TOPIC, packet };
    if (Array.isArray(options.destinationIdentities) && options.destinationIdentities.length) body.to = options.destinationIdentities;
    try {
      const response = await fetch(`${base}/packets`, {
        method: 'POST',
        credentials: 'include',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        // Refused (not allowed, or our state was out of date): take the server's state.
        if (data && data.snapshot) applyAuthoritativeWatchSnapshot(data.snapshot);
      }
    } catch {}
  }

  function applyActivity(activity) {
    if (!activity) return;
    const meIdentity = room.localParticipant ? room.localParticipant.identity : '';
    const next = new Map();
    (activity.participants || []).forEach((p) => {
      if (String(p.identity) === meIdentity) {
        if (room.localParticipant) room.localParticipant.attributes = participantFrom(p).attributes;
        return;
      }
      const current = room.remoteParticipants.get(String(p.identity));
      const fresh = participantFrom(p);
      next.set(fresh.identity, current && sameParticipant(current, fresh) ? current : fresh);
    });
    const before = room.remoteParticipants;
    room.remoteParticipants = next;
    next.forEach((p, identity) => {
      if (!before.has(identity)) emit('participantConnected', p);
    });
    before.forEach((p, identity) => {
      if (!next.has(identity)) emit('participantDisconnected', p);
    });
    applyWatchControl({ host: activity.hostUserId ? `u${activity.hostUserId}` : '', controlMode: activity.controlMode });
    if (typeof callbacks.onActivity === 'function') callbacks.onActivity(activity);
  }

  function onEvent(event) {
    if (!event || closed) return;
    if (event.type === 'hello') {
      const me = (event.activity.participants || []).find((p) => String(p.identity) === String(event.you.identity));
      room.localParticipant = {
        identity: String(event.you.identity),
        name: me ? me.name : '',
        attributes: me ? participantFrom(me).attributes : {},
        publishData: publish,
      };
      setConnection('connected');
      applyActivity(event.activity);
      return;
    }
    if (event.type === 'snapshot') {
      applyAuthoritativeWatchSnapshot(event.packet);
      return;
    }
    if (event.type === 'packet') {
      const participant = room.remoteParticipants.get(String(event.from)) || { identity: String(event.from), attributes: {} };
      const bytes = encoder.encode(JSON.stringify(event.packet));
      if (event.topic === SPOTIFY_TOPIC) applyIncomingRemoteSpotifyPlaybackPacket(bytes, participant, event.topic);
      else applyIncomingWatchQueuePacket(bytes, participant, room, event.topic);
      return;
    }
    if (event.type === 'activity') {
      applyActivity(event.activity);
      return;
    }
    if (event.type === 'ended') {
      disconnect();
      if (typeof callbacks.onEnded === 'function') callbacks.onEnded(event.reason || 'ended');
      return;
    }
    if (event.type === 'kicked') {
      disconnect();
      if (typeof callbacks.onKicked === 'function') callbacks.onKicked();
      return;
    }
    if (event.type === 'left') disconnect();
  }

  function connect() {
    if (closed || source) return room;
    bindWatchTransport(room);
    resetWatchQueueState(room);
    setConnection('connecting');
    if (typeof window === 'undefined' || typeof window.EventSource === 'undefined') {
      setConnection('closed');
      return room;
    }
    source = new window.EventSource(`${base}/events`, { withCredentials: true });
    source.onmessage = (message) => {
      try {
        onEvent(JSON.parse(String(message.data || '{}')));
      } catch {}
    };
    source.onerror = () => {
      if (closed) return;
      // EventSource retries by itself; CLOSED means the server refused the stream (session gone or not a member).
      if (source && source.readyState === 2) setConnection('closed');
      else setConnection('reconnecting');
    };
    return room;
  }

  function disconnect() {
    if (closed) return;
    closed = true;
    if (source) source.close();
    source = null;
    resetWatchQueueState(room);
    unbindWatchTransport(room);
    room.localParticipant = null;
    room.remoteParticipants = new Map();
    setConnection('closed');
  }

  return room;
}
